import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Input,
  Tag,
  Modal,
  Progress,
  message,
  Spin
} from 'antd';
import {
  Video,
  RefreshCw,
  Search
} from 'lucide-react';
import axios from "../../utils/axios";
import { webSocketUrl } from '../../constants/config';

const { Search: AntSearch } = Input;

const CameraStatus = () => {
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [frameStats, setFrameStats] = useState({ fps: 0, dropped: 0 });
  
  const wsRef = useRef(null);
  const canvasRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem("user"));
  const username = user?.username;
  const organizationId = localStorage.getItem('organizationId');

  // FPS tracking with rolling average
  const frameTimestampsRef = useRef([]);
  const frameCountRef = useRef(0);
  const droppedCountRef = useRef(0);
  const fpsIntervalRef = useRef(null);

  // Frame rendering
  const isRenderingRef = useRef(false);
  const lastFrameDataRef = useRef(null);

  // ---------------- FETCH CAMERAS ----------------
  const fetchCameras = async () => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-cameras", { organizationId, username });
      if (res.data.success) {
        setCameras(res.data.cameras || []);
      }
    } catch {
      message.error("Error fetching cameras");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (username) fetchCameras();
  }, [username]);

  // ---------------- RENDER FRAME ----------------
  const renderFrame = (frameData) => {
    if (isRenderingRef.current) {
      // If already rendering, store this frame to render next
      lastFrameDataRef.current = frameData;
      droppedCountRef.current++;
      return;
    }

    isRenderingRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) {
      isRenderingRef.current = false;
      return;
    }

    const ctx = canvas.getContext("2d", { alpha: false });
    const img = new Image();

    img.onload = () => {
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Track frame timing
        const now = performance.now();
        frameTimestampsRef.current.push(now);
        
        // Keep only last 10 timestamps
        if (frameTimestampsRef.current.length > 10) {
          frameTimestampsRef.current.shift();
        }
        
        frameCountRef.current++;
        setStreamLoading(false);
      } catch (error) {
        console.error("Render error:", error);
      } finally {
        isRenderingRef.current = false;
        
        // Render queued frame if exists
        if (lastFrameDataRef.current) {
          const queuedFrame = lastFrameDataRef.current;
          lastFrameDataRef.current = null;
          renderFrame(queuedFrame);
        }
      }
    };

    img.onerror = () => {
      console.error("Failed to load frame");
      isRenderingRef.current = false;
    };

    img.src = `data:image/jpeg;base64,${frameData}`;
  };

  // ---------------- CALCULATE FPS ----------------
  const calculateFPS = () => {
    const timestamps = frameTimestampsRef.current;
    
    if (timestamps.length < 2) {
      return 0;
    }
    
    // Calculate FPS from actual frame timestamps
    const timeSpan = (timestamps[timestamps.length - 1] - timestamps[0]) / 1000; // Convert to seconds
    const frameSpan = timestamps.length - 1;
    
    return timeSpan > 0 ? frameSpan / timeSpan : 0;
  };

  // ---------------- WEBSOCKET STREAM ----------------
  const fetchLiveStream = async (camera) => {
    try {
      setStreamLoading(true);

      // Validate camera
      await axios.get('/get-live-stream', {
        params: {
          ipAddress: camera.ipAddress,
          channel: camera.channel
        }
      });

      // Close existing WebSocket
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(webSocketUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({
          ipAddress: camera.ipAddress,
          channel: camera.channel
        }));
 
        // Reset counters
        frameCountRef.current = 0;
        droppedCountRef.current = 0;
        frameTimestampsRef.current = [];

        // FPS update interval - every second
        fpsIntervalRef.current = setInterval(() => {
          const fps = calculateFPS();
          const dropped = droppedCountRef.current;
          
          setFrameStats({ 
            fps: Math.round(fps * 10) / 10, // Round to 1 decimal
            dropped 
          });
          
          // Reset dropped counter every second
          droppedCountRef.current = 0;
        }, 1000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== "frame") return;

          renderFrame(data.frame);
        } catch (error) {
          console.error("WebSocket message error:", error);
        }
      };

      ws.onerror = (err) => {
        console.error("WebSocket error:", err);
        message.error("WebSocket streaming error");
        cleanupStream();
      };

      ws.onclose = () => {
        cleanupStream();
      };

    } catch (error) {
      console.error("Live stream error:", error);
      message.error("Unable to start live stream");
      cleanupStream();
    }
  };

  // ---------------- CLEANUP ----------------
  const cleanupStream = () => {
    // Clear FPS counter
    if (fpsIntervalRef.current) {
      clearInterval(fpsIntervalRef.current);
      fpsIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    // Reset state
    isRenderingRef.current = false;
    lastFrameDataRef.current = null;
    frameCountRef.current = 0;
    droppedCountRef.current = 0;
    frameTimestampsRef.current = [];
    setFrameStats({ fps: 0, dropped: 0 });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupStream();
  }, []);

  const filteredCameras = cameras.filter(camera =>
    camera.cameraName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    camera.projectName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            Camera Status Dashboard
          </h1>
          <p className="text-gray-600">
            Monitor and manage all surveillance cameras
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex gap-4">
          <AntSearch
            placeholder="Search cameras..."
            prefix={<Search className="w-4 h-4" />}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
          <Button
            icon={<RefreshCw className="w-4 h-4" />}
            loading={loading}
            onClick={fetchCameras}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Camera Cards */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin tip="Loading cameras..." size="large" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCameras.length > 0 ? (
            filteredCameras.map((camera) => (
              <Card
                key={camera._id}
                className="cursor-pointer hover:shadow-lg"
                onClick={() => {
                  setSelectedCamera(camera);
                  setIsModalVisible(true);
                  fetchLiveStream(camera);
                }}
              >
                <div className="bg-gray-900 rounded-lg aspect-video flex items-center justify-center mb-4">
                  <Video className="w-10 h-10 text-gray-500" />
                </div>

                <h3 className="font-semibold text-lg">{camera.cameraName}</h3>

                <div className="text-sm text-gray-600 space-y-1 mt-2">
                  <p><strong>IP:</strong> {camera.ipAddress}</p>
                  <p><strong>Port:</strong> {camera.port}</p>
                  <p><strong>Channel:</strong> {camera.channel}</p>
                  <p><strong>Project:</strong> {camera.projectName}</p>
                </div>

                <div className="flex justify-between items-center mt-3">
                  <Tag color="green">ONLINE</Tag>
                  <span className="text-xs text-gray-500">Live</span>
                </div>

                <Progress percent={100} showInfo={false} size="small" />
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500">
              No cameras found
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          cleanupStream();
        }}
        footer={null}
        title={selectedCamera?.cameraName}
        width={900}
        centered
      >
        <div
          className="relative bg-black rounded-lg overflow-hidden mb-4 flex justify-center items-center"
          style={{ height: 500 }}
        >
          <canvas
            ref={canvasRef}
            width={1280}
            height={720}
            className="max-w-full max-h-full rounded"
            style={{ imageRendering: 'auto' }}
          />

          {streamLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-lg animate-pulse">
                Loading stream…
              </span>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CameraStatus;